import { MANAGED_AI_CONNECTOR_ID } from "../../shared/aiProviders/managedAiConnector";
import {
  DEFAULT_MODEL_PRICING,
  MANAGED_MODEL_PRICING_ESTIMATES,
} from "../../shared/aiModelPricing";

export type ConnectorUsageRow = {
  connectorId: string;
  measure: string;
  dimension: string | null;
  value: number | null;
  source?: string | null;
};

// Rows written before sources were recorded carry ""; fold them into "other"
// rather than surfacing an unnamed bucket.
export function usageRowSource(row: ConnectorUsageRow): string {
  return row.source || "other";
}

export type ConnectorUsageDayRow = ConnectorUsageRow & { day: number };

export const NO_SPACE = "__no-space__";

export type SpaceAttributedRow = {
  workflowId: string | null;
  workflow?: { space?: { id: string } | null } | null;
};

export function filterRowsBySpace<T extends SpaceAttributedRow>(
  rows: readonly T[],
  spaceId: string | null
): readonly T[] {
  if (spaceId === null) return rows;
  if (spaceId === NO_SPACE) return rows.filter((r) => r.workflowId === null);
  return rows.filter((r) => r.workflow?.space?.id === spaceId);
}

export type TransportTotals = {
  requests: number;
  errors: number;
  bytesIn: number;
  bytesOut: number;
  durationMs: number;
};

const TRANSPORT_KEYS: Record<string, keyof TransportTotals> = {
  requests: "requests",
  errors: "errors",
  bytes_in: "bytesIn",
  bytes_out: "bytesOut",
  duration_ms: "durationMs",
};

const TOKEN_KEYS = {
  input_tokens: "inputTokens",
  output_tokens: "outputTokens",
  cache_read_tokens: "cacheReadTokens",
  cache_write_tokens: "cacheWriteTokens",
} as const;

type TokenKey = (typeof TOKEN_KEYS)[keyof typeof TOKEN_KEYS];

export type ModelTokenTotals = {
  model: string;
  totalTokens: number;
} & Record<TokenKey, number>;

export function computeTransportTotals(
  rows: readonly ConnectorUsageRow[]
): TransportTotals | null {
  let any = false;
  const totals: TransportTotals = {
    requests: 0,
    errors: 0,
    bytesIn: 0,
    bytesOut: 0,
    durationMs: 0,
  };
  for (const row of rows) {
    const key = TRANSPORT_KEYS[row.measure];
    if (!key) continue;
    any = true;
    totals[key] += row.value ?? 0;
  }
  return any ? totals : null;
}

export function computeTokensByModel(
  rows: readonly ConnectorUsageRow[]
): ModelTokenTotals[] {
  const byModel = new Map<string, ModelTokenTotals>();
  for (const row of rows) {
    const key = TOKEN_KEYS[row.measure as keyof typeof TOKEN_KEYS];
    if (!key) continue;
    const model = row.dimension ?? "";
    let entry = byModel.get(model);
    if (!entry) {
      entry = {
        model,
        inputTokens: 0,
        outputTokens: 0,
        cacheReadTokens: 0,
        cacheWriteTokens: 0,
        totalTokens: 0,
      };
      byModel.set(model, entry);
    }
    entry[key] += row.value ?? 0;
  }
  for (const entry of byModel.values()) {
    entry.totalTokens = entry.inputTokens + entry.outputTokens;
  }
  return [...byModel.values()].sort(
    (a, b) => b.totalTokens - a.totalTokens || a.model.localeCompare(b.model)
  );
}

export type ConnectorUsageSummary = {
  connectorId: string;
  requests: number;
  errors: number;
  bytes: number;
  tokens: number | null;
};

// Per-connector token totals come solely from the proxy-metered
// connector_usage_daily ledger. ai_usage_records is the AI-provider spend ledger
// and is deliberately not consulted here.
export function computeConnectorUsageSummaries(
  rows: readonly ConnectorUsageRow[]
): ConnectorUsageSummary[] {
  const byConnector = new Map<string, ConnectorUsageSummary>();
  const entryFor = (connectorId: string): ConnectorUsageSummary => {
    let entry = byConnector.get(connectorId);
    if (!entry) {
      entry = { connectorId, requests: 0, errors: 0, bytes: 0, tokens: null };
      byConnector.set(connectorId, entry);
    }
    return entry;
  };

  for (const row of rows) {
    const entry = entryFor(row.connectorId);
    const value = row.value ?? 0;
    if (row.measure === "requests") entry.requests += value;
    else if (row.measure === "errors") entry.errors += value;
    else if (row.measure === "bytes_in" || row.measure === "bytes_out") {
      entry.bytes += value;
    } else if (
      row.measure === "input_tokens" ||
      row.measure === "output_tokens"
    ) {
      entry.tokens = (entry.tokens ?? 0) + value;
    }
  }

  return [...byConnector.values()].sort(
    (a, b) =>
      b.requests - a.requests ||
      (b.tokens ?? 0) - (a.tokens ?? 0) ||
      a.connectorId.localeCompare(b.connectorId)
  );
}

export type SourceUsageSummary = {
  source: string;
  requests: number;
  errors: number;
  bytes: number;
  tokens: number | null;
};

export function computeUsageBySource(
  rows: readonly ConnectorUsageRow[]
): SourceUsageSummary[] {
  const bySource = new Map<string, SourceUsageSummary>();
  for (const row of rows) {
    const source = usageRowSource(row);
    let entry = bySource.get(source);
    if (!entry) {
      entry = { source, requests: 0, errors: 0, bytes: 0, tokens: null };
      bySource.set(source, entry);
    }
    const value = row.value ?? 0;
    if (row.measure === "requests") entry.requests += value;
    else if (row.measure === "errors") entry.errors += value;
    else if (row.measure === "bytes_in" || row.measure === "bytes_out") {
      entry.bytes += value;
    } else if (
      row.measure === "input_tokens" ||
      row.measure === "output_tokens"
    ) {
      entry.tokens = (entry.tokens ?? 0) + value;
    }
  }
  return [...bySource.values()].sort(
    (a, b) => b.requests - a.requests || a.source.localeCompare(b.source)
  );
}

export type ConnectorUsageOverview = {
  requests: number;
  errors: number;
  bytes: number;
  tokens: number | null;
  inputTokens: number;
  cacheReadTokens: number;
};

export function computeConnectorUsageOverview(
  summaries: readonly ConnectorUsageSummary[],
  rows: readonly ConnectorUsageRow[]
): ConnectorUsageOverview {
  const overview: ConnectorUsageOverview = {
    requests: 0,
    errors: 0,
    bytes: 0,
    tokens: null,
    inputTokens: 0,
    cacheReadTokens: 0,
  };
  for (const summary of summaries) {
    overview.requests += summary.requests;
    overview.errors += summary.errors;
    overview.bytes += summary.bytes;
    if (summary.tokens != null) {
      overview.tokens = (overview.tokens ?? 0) + summary.tokens;
    }
  }
  // Cache hit rate is only meaningful for proxy-metered tokens, so it reads the
  // ledger rows directly rather than the summaries' ai_usage fallback total.
  for (const row of rows) {
    if (row.measure === "input_tokens") overview.inputTokens += row.value ?? 0;
    else if (row.measure === "cache_read_tokens")
      overview.cacheReadTokens += row.value ?? 0;
  }
  return overview;
}

const DAY_MS = 86_400_000;

export type DailyRequestVolume = {
  day: number;
  total: number;
  errors: number;
  byConnector: Map<string, number>;
};

export function computeRequestVolumeByDay(
  rows: readonly ConnectorUsageDayRow[],
  since: number,
  now: number
): DailyRequestVolume[] {
  const firstDay = Math.ceil(since / DAY_MS) * DAY_MS;
  const lastDay = Math.floor(now / DAY_MS) * DAY_MS;
  const byDay = new Map<
    number,
    { errors: number; byConnector: Map<string, number> }
  >();
  for (let day = firstDay; day <= lastDay; day += DAY_MS) {
    byDay.set(day, { errors: 0, byConnector: new Map() });
  }
  for (const row of rows) {
    const entry = byDay.get(row.day);
    if (!entry) continue;
    if (row.measure === "requests") {
      entry.byConnector.set(
        row.connectorId,
        (entry.byConnector.get(row.connectorId) ?? 0) + (row.value ?? 0)
      );
    } else if (row.measure === "errors") entry.errors += row.value ?? 0;
  }
  return [...byDay.entries()].map(([day, { errors, byConnector }]) => {
    let total = 0;
    for (const requests of byConnector.values()) total += requests;
    return { day, total, errors, byConnector };
  });
}

export type UsagePriceRow = {
  spaceId: string | null;
  connectorId: string | null;
  measure: string;
  dimension: string | null;
  amountPerUnit: number;
};

const MEASURE_PRICING_FIELD = {
  input_tokens: "inputPerMillion",
  output_tokens: "outputPerMillion",
  cache_read_tokens: "cacheReadPerMillion",
  cache_write_tokens: "cacheWritePerMillion",
} as const;

// List price per single metered token for a known model, used as a fallback
// when no rate has been entered.
export function defaultPricePerUnit(
  measure: string,
  model: string
): number | null {
  const field =
    MEASURE_PRICING_FIELD[measure as keyof typeof MEASURE_PRICING_FIELD];
  if (!field) return null;
  const perMillion = DEFAULT_MODEL_PRICING[model]?.[field];
  return perMillion == null ? null : perMillion / 1_000_000;
}

// The USD amount is stored per single metered unit; the pricing UI enters and
// displays it scaled by each measure's natural unit (per 1M tokens, per 1K
// requests, per GB). A measure absent from this map has no priceable unit.
export const MEASURE_UNIT: Record<
  string,
  { per: number; label: string; name: string }
> = {
  input_tokens: { per: 1_000_000, label: "$/1M tokens", name: "Input" },
  output_tokens: { per: 1_000_000, label: "$/1M tokens", name: "Output" },
  cache_read_tokens: {
    per: 1_000_000,
    label: "$/1M tokens",
    name: "Cache read",
  },
  cache_write_tokens: {
    per: 1_000_000,
    label: "$/1M tokens",
    name: "Cache write",
  },
  requests: { per: 1_000, label: "$/1K requests", name: "Requests" },
  bytes_in: { per: 1_000_000_000, label: "$/GB", name: "Data in" },
  bytes_out: { per: 1_000_000_000, label: "$/GB", name: "Data out" },
};

function priceKey(connectorId: string, measure: string, dimension: string) {
  return `${connectorId}\0${measure}\0${dimension}`;
}

// The managed provider has no connector_usage_prices rows, so its sentinel
// spend is priced from the published list-price estimates (per-1M → per-unit).
export function managedAiDefaultPrices(): Map<string, number> {
  const map = new Map<string, number>();
  for (const [model, pricing] of Object.entries(
    MANAGED_MODEL_PRICING_ESTIMATES
  )) {
    const set = (measure: string, perMillion: number | undefined) => {
      if (perMillion == null) return;
      map.set(
        priceKey(MANAGED_AI_CONNECTOR_ID, measure, model),
        perMillion / 1_000_000
      );
    };
    set("input_tokens", pricing.inputPerMillion);
    set("output_tokens", pricing.outputPerMillion);
    set("cache_read_tokens", pricing.cacheReadPerMillion);
    set("cache_write_tokens", pricing.cacheWritePerMillion);
  }
  return map;
}

// Rows pinned to a space or AI provider are ignored — connector spend prices
// the connector-wide total.
export function buildUsagePriceLookup(
  rows: readonly UsagePriceRow[],
  defaults?: ReadonlyMap<string, number>
): Map<string, number> {
  const map = new Map<string, number>(defaults);
  for (const row of rows) {
    if (row.connectorId == null) continue;
    if (row.spaceId != null) continue;
    map.set(
      priceKey(row.connectorId, row.measure, row.dimension ?? ""),
      row.amountPerUnit
    );
  }
  return map;
}

function addCost(
  map: Map<string, number | null>,
  key: string,
  cost: number | null
) {
  if (cost == null) {
    if (!map.has(key)) map.set(key, null);
    return;
  }
  map.set(key, (map.get(key) ?? 0) + cost);
}

export type ConnectorCosts = {
  byConnector: Map<string, number | null>;
  byModel: Map<string, number | null>;
  bySource: Map<string, number | null>;
  total: number | null;
};

function priceFor(
  prices: ReadonlyMap<string, number>,
  connectorId: string,
  measure: string,
  dimension: string
): number | null {
  return (
    prices.get(priceKey(connectorId, measure, dimension)) ??
    defaultPricePerUnit(measure, dimension) ??
    null
  );
}

// A rollup key is null only when none of its rows were priced, so an unpriced
// connector reads as "—" rather than $0.
export function computeConnectorCosts(
  rows: readonly ConnectorUsageRow[],
  prices: ReadonlyMap<string, number>
): ConnectorCosts {
  const byConnector = new Map<string, number | null>();
  const byModel = new Map<string, number | null>();
  const bySource = new Map<string, number | null>();
  let total: number | null = null;
  const addTotal = (cost: number | null) => {
    if (cost != null) total = (total ?? 0) + cost;
  };

  // input_tokens is the grand total that already includes cache reads and
  // writes, so it's netted down to the fresh remainder before the input rate
  // applies (matching estimateUsageCostUsd) or cached tokens bill twice. Netting
  // is grouped by (connector, model, source) so each rollup gets its own slice.
  //
  // cachedInput sums reads and writes only to subtract them from the grand
  // total; it is never priced here. The reads and writes keep their own
  // (different) rates in the main loop below, priced from their own rows.
  const inputGroups = new Map<
    string,
    {
      connectorId: string;
      dimension: string;
      source: string;
      input: number;
      cachedInput: number;
    }
  >();
  for (const row of rows) {
    if (
      row.measure !== "input_tokens" &&
      row.measure !== "cache_read_tokens" &&
      row.measure !== "cache_write_tokens"
    ) {
      continue;
    }
    const dimension = row.dimension ?? "";
    const source = usageRowSource(row);
    const key = `${row.connectorId}\0${dimension}\0${source}`;
    let group = inputGroups.get(key);
    if (!group) {
      group = {
        connectorId: row.connectorId,
        dimension,
        source,
        input: 0,
        cachedInput: 0,
      };
      inputGroups.set(key, group);
    }
    if (row.measure === "input_tokens") group.input += row.value ?? 0;
    else group.cachedInput += row.value ?? 0;
  }

  for (const row of rows) {
    if (row.measure === "input_tokens") continue;
    const dimension = row.dimension ?? "";
    const price = priceFor(prices, row.connectorId, row.measure, dimension);
    const cost = price == null ? null : (row.value ?? 0) * price;
    addCost(byConnector, row.connectorId, cost);
    addCost(bySource, usageRowSource(row), cost);
    if (TOKEN_KEYS[row.measure as keyof typeof TOKEN_KEYS]) {
      addCost(byModel, dimension, cost);
    }
    addTotal(cost);
  }

  for (const group of inputGroups.values()) {
    const price = priceFor(
      prices,
      group.connectorId,
      "input_tokens",
      group.dimension
    );
    const fresh = Math.max(0, group.input - group.cachedInput);
    const cost = price == null ? null : fresh * price;
    addCost(byConnector, group.connectorId, cost);
    addCost(bySource, group.source, cost);
    addCost(byModel, group.dimension, cost);
    addTotal(cost);
  }

  return { byConnector, byModel, bySource, total };
}

export const TOKEN_MEASURES: ReadonlySet<string> = new Set(
  Object.keys(TOKEN_KEYS)
);

export type UsageBudgetRow = {
  id: string;
  spaceId: string | null;
  connectorId: string | null;
  measure: string;
  limitAmount: number;
  // Zero clients can't see the column's default, so it reads back nullable.
  warnThresholdPct: number | null;
};

export type BudgetLevel = "ok" | "warn" | "over";

export type BudgetStatus = {
  used: number | null;
  limit: number;
  warnThresholdPct: number;
  level: BudgetLevel;
};

export function budgetLevel(
  used: number | null,
  limit: number,
  warnThresholdPct: number
): BudgetLevel {
  if (used == null) return "ok";
  if (used >= limit) return "over";
  return (used / limit) * 100 >= warnThresholdPct ? "warn" : "ok";
}

// Connector spend budgets scoped to `spaceId` (null = the connector-wide total),
// matched against costs the caller has already computed for that same scope.
export function computeBudgetStatuses(
  costs: ReadonlyMap<string, number | null>,
  budgets: readonly UsageBudgetRow[],
  spaceId: string | null
): Map<string, BudgetStatus> {
  const statuses = new Map<string, BudgetStatus>();
  for (const budget of budgets) {
    if (budget.connectorId == null) continue;
    if (budget.spaceId !== spaceId) continue;
    if (budget.measure !== "spend_usd") continue;
    const used = costs.get(budget.connectorId) ?? null;
    const warnThresholdPct = budget.warnThresholdPct ?? 80;
    statuses.set(budget.connectorId, {
      used,
      limit: budget.limitAmount,
      warnThresholdPct,
      level: budgetLevel(used, budget.limitAmount, warnThresholdPct),
    });
  }
  return statuses;
}

export function formatAverageDuration(
  durationMs: number,
  requests: number
): string {
  if (requests === 0) return "—";
  const avg = durationMs / requests;
  if (avg < 1000) return `${Math.round(avg)} ms`;
  return `${(avg / 1000).toFixed(1)} s`;
}
